import { useState, useMemo, type ReactNode } from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { clsx } from 'clsx';
import EmptyState from './EmptyState';

export interface Column<T> {
  key: string;
  header: string;
  render: (row: T) => ReactNode;
  sortable?: boolean;
  className?: string;
  sortValue?: (row: T) => string | number | null | undefined;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  keyExtractor: (row: T) => string;
  onRowClick?: (row: T) => void;
  page?: number;
  pageSize?: number;
  total?: number;
  onPageChange?: (page: number) => void;
  emptyTitle?: string;
  emptyDescription?: string;
  className?: string;
  sortKey?: string | null;
  sortDir?: 'asc' | 'desc';
  onSortChange?: (key: string, dir: 'asc' | 'desc') => void;
}

export default function DataTable<T>({
  columns,
  data,
  keyExtractor,
  onRowClick,
  page,
  pageSize,
  total,
  onPageChange,
  emptyTitle = 'No data found',
  emptyDescription,
  className,
  sortKey: controlledSortKey,
  sortDir: controlledSortDir,
  onSortChange,
}: DataTableProps<T>) {
  const [localSortKey, setLocalSortKey] = useState<string | null>(null);
  const [localSortDir, setLocalSortDir] = useState<'asc' | 'desc'>('asc');

  const isControlled = onSortChange !== undefined;
  const sortKey = isControlled ? controlledSortKey ?? null : localSortKey;
  const sortDir = isControlled ? controlledSortDir ?? 'asc' : localSortDir;

  const sortedData = useMemo(() => {
    if (isControlled || !sortKey) return data;
    const col = columns.find((c) => c.key === sortKey);
    const getter = col?.sortValue
      ? col.sortValue
      : (row: T) => (row as Record<string, unknown>)[sortKey];
    return [...data].sort((a, b) => {
      const aVal = getter(a);
      const bVal = getter(b);
      const aMissing = aVal == null || aVal === '';
      const bMissing = bVal == null || bVal === '';
      if (aMissing && bMissing) return 0;
      if (aMissing) return 1;
      if (bMissing) return -1;
      let cmp: number;
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        cmp = aVal - bVal;
      } else {
        cmp = String(aVal).localeCompare(String(bVal), undefined, { numeric: true });
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [data, sortKey, sortDir, columns, isControlled]);

  const handleSort = (key: string) => {
    const nextDir: 'asc' | 'desc' = sortKey === key ? (sortDir === 'asc' ? 'desc' : 'asc') : 'asc';
    if (isControlled) {
      onSortChange?.(key, nextDir);
    } else if (sortKey === key) {
      setLocalSortDir(nextDir);
    } else {
      setLocalSortKey(key);
      setLocalSortDir(nextDir);
    }
  };

  const totalPages = total != null && pageSize ? Math.ceil(total / pageSize) : undefined;
  const showPagination = totalPages != null && totalPages > 1 && page != null;

  if (data.length === 0) {
    return <EmptyState title={emptyTitle} description={emptyDescription} />;
  }

  return (
    <div className={clsx('overflow-hidden rounded-2xl border border-slate-200/60 bg-white dark:border-slate-800/80 dark:bg-slate-900', className)}>
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead>
            <tr className="border-b border-slate-100 dark:border-slate-800/60">
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={clsx(
                    'px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500',
                    col.sortable && 'cursor-pointer select-none transition-colors hover:text-slate-600 dark:hover:text-slate-300',
                    col.className,
                  )}
                  onClick={col.sortable ? () => handleSort(col.key) : undefined}
                >
                  <div className="flex items-center gap-1">
                    {col.header}
                    {col.sortable && (
                      <span className="text-slate-300 dark:text-slate-600">
                        {sortKey === col.key ? (
                          sortDir === 'asc' ? <ChevronUp className="h-3 w-3 text-brand-500" /> : <ChevronDown className="h-3 w-3 text-brand-500" />
                        ) : (
                          <ChevronsUpDown className="h-3 w-3" />
                        )}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50 dark:divide-slate-800/40">
            {sortedData.map((row) => (
              <tr
                key={keyExtractor(row)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={clsx(
                  'transition-colors duration-100',
                  onRowClick && 'cursor-pointer hover:bg-surface-50 dark:hover:bg-slate-800/30',
                )}
              >
                {columns.map((col) => (
                  <td key={col.key} className={clsx('whitespace-nowrap px-5 py-3.5 text-sm text-slate-700 dark:text-slate-300', col.className)}>
                    {col.render(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showPagination && (
        <div className="flex items-center justify-between border-t border-slate-100 px-5 py-3 dark:border-slate-800/60">
          <p className="text-[13px] text-slate-500 dark:text-slate-400">
            {((page - 1) * (pageSize ?? 0)) + 1}–{Math.min(page * (pageSize ?? 0), total ?? 0)} of {total}
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => onPageChange?.(page - 1)}
              disabled={page <= 1}
              className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40 dark:text-slate-500 dark:hover:bg-slate-800"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="min-w-[3rem] rounded-lg bg-surface-100 px-2.5 py-1 text-center text-xs font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-400">
              {page} / {totalPages}
            </span>
            <button
              onClick={() => onPageChange?.(page + 1)}
              disabled={page >= (totalPages ?? 1)}
              className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40 dark:text-slate-500 dark:hover:bg-slate-800"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
